import React, { useEffect, useState } from "react";
import { Alert, Box, Grid2 } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import {
  clearFloors,
  clearInputs,
  setInputs
} from "../../features/Booking-Form/slices/booking.slice";
import { NotSerializedBookingMode } from "../../features/Booking-Form/typings/booking-inputs";
import { LocationInventory } from "../../features/FloorManager/typings/location-inventory";
import { useRemoteFetchCompanyLocations } from "../../hooks/Remote/Location/useRemoteFetchCompanyLocations";
import { getBookingType } from "./utils/booking.utils";
import BookingSettingsView from "./BookingSettingsView/BookingSettingsView";
import { FormSelectionBookingType } from "./form-selection-bookingType.component";

/**
 * @description Component that displays the various booking modes.
 * @param props.setInputs is the main state input update.
 * @param props.changeAction is the function that cleans up state after going a step back.
 * @version 0.1.0
 */

type Props = {
  nextStep: () => void;
  changeAction?: () => void;
};

const FormSelection: React.FC<Props> = ({ nextStep, changeAction }) => {
  const { t } = useTranslation();

  const {
    userInformation: { sub, surname, firstName, email },
    settings: { preferredLocations }
  } = useSelector((state: RootState) => state.login);
  const { inputs } = useSelector((state: RootState) => state.booking);
  const dispatch = useDispatch();

  const [doubleCheckLocations, setDoubleCheckLocations] = useState<LocationInventory[]>([]);

  const { data: companyLocations, isLoading: companyLocationsLoading } =
    useRemoteFetchCompanyLocations({
      isFinanceRequest: false,
      noMobileWorking: true,
      isBookingSettings: true
    });

  /**
   * @description function that passes the current user as person who is booking.
   * @param mode
   */
  const onModeChange = (mode: NotSerializedBookingMode) => {
    changeAction?.();
    dispatch(clearInputs());
    dispatch(clearFloors());

    dispatch(
      setInputs({
        mode: mode,
        bookingType: getBookingType(mode),
        usersBookedFor: [
          {
            userId: sub,
            firstName,
            surname: surname,
            isExternal: false,
            email: email
          }
        ],
        isUserBookedBy: true,
        automatedSeating: false
      })
    );
    nextStep();
  };

  const checkPreferredAndDouble =
    preferredLocations && preferredLocations.length > 0 && doubleCheckLocations.length > 0;

  useEffect(() => {
    if (companyLocations) setDoubleCheckLocations(companyLocations);
  }, [companyLocationsLoading, companyLocations]);

  useEffect(() => {
    if (inputs.booked) {
      dispatch(clearInputs());
    }
  }, [inputs, dispatch]);

  return (
    <Grid2 justifyContent="center" container data-testid="form-selection-container">
      <Box
        display="flex"
        justifyContent="center"
        alignItems={"center"}
        data-testid="form-selection-box"
        width={"100%"}
        flexDirection={"column"}
      >
        <BookingSettingsView />

        {checkPreferredAndDouble ? (
          <Grid2
            data-testid="select-booking-mode"
            container
            sx={{
              mt: 1,
              width: "100%",
              justifyContent: "center",
              alignItems: "stretch"
            }}
          >
            <Grid2
              data-testid="select-booking-mode-child-box"
              sx={{
                width: "75vw",
                display: "flex",
                justifyContent: "center",
                flexDirection: "row",
                flexWrap: "wrap"
              }}
            >
              <FormSelectionBookingType onModeChange={onModeChange} />
            </Grid2>
          </Grid2>
        ) : (
          <Alert style={{ marginTop: 10 }} severity="warning">
            {t("Please select preferred locations")}
          </Alert>
        )}
      </Box>
    </Grid2>
  );
};

export default FormSelection;
